import { Box, Grid, Typography, Icon, Button } from '@mui/material';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { DataGrid, GridCellParams, GridColDef, GridEventListener } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useRecoilValue } from 'recoil';
import { styled } from 'styled-components';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  IComanda,
  IComandaEtapaReunioes,
  IEtapa,
  StatusReuniaoEnum,
} from '../../../../../commons/types/crm';
import { authAtom } from '../../../../../states/usuarios/AuthState';
import { useUsuarioActions } from '../../../../../states/usuarios/usuario.actions';
import { IAuth, ModeloComercial } from '../../../../../commons/types/usuatios';
import { ButtonStato } from '../../../../../commons/styleds/buttons';
import DrawerReunioes from './DrawerReunioes';
import { useReuniaoActions } from '../../../../../states/reunioes/reuniao.actions';
import { handleStatus } from '../../../../Dashboard/TableReunioes';
import { useLocation } from 'react-router-dom';
import { esES, ptBR } from '@mui/material/locale';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from '../../../../../commons/useTranslasion';
import { useDateFormatters } from '../../../../../utils/useDateFormatters';

dayjs.extend(utc);
dayjs.extend(timezone);

const empty: IComandaEtapaReunioes = {
  id: 0,
  tbEtapaId: 0,
  onboarding: false,
  dtRealizadaInicio: undefined,
  dtRealizadaFim: undefined,
  idConsultorResponsavel: 0,
  valorHora: undefined,
  tempoTotal: '',
  valorTotal: '',
  dtPrevista: dayjs(new Date()),
} as IComandaEtapaReunioes;

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

interface IParams {
  idComanda?: number;
  etapas?: IEtapa[];
  comandaEtapas?: IComandaEtapaReunioes[];
  comanda?: IComanda;
  loading: boolean;
  carregarComanda: Function;
}

const ReuniaoComponent: React.FC<IParams> = (props) => {
  const { translate, language } = useTranslation();
  const { formatarData, formatarHora } = useDateFormatters();
  const lang = language ?? "pt-br";
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }
  
  const { idComanda, etapas, comandaEtapas, comanda, loading, carregarComanda } = props;
  const auth = useRecoilValue(authAtom);
  const consultorActions = useUsuarioActions();
  const reunioesActions = useReuniaoActions();
  const [open, setOpen] = useState<boolean>(false);
  const [editando, setEditando] = useState<boolean>(false);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [reunioes, setReunioes] = useState<IComandaEtapaReunioes[]>([]);
  const [etapasSelect, setEtapasSelect] = useState<IEtapa[]>([]);
  const [reuniao, setReuniao] = useState<IComandaEtapaReunioes>(empty);
  const [consultores, setConsultores] = useState<IAuth[]>([]);
  const [paginationLoading, setPaginationLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });
  const [visibleRows, setVisibleRows] = useState<number>(10);
  const query = useQuery();
  const reuniaoId = query.get('reuniao');

  useEffect(() => {
    const comandaEtapaList = comandaEtapas?.filter((f) => f.idConsultorResponsavel! > 0);
    setReunioes(comandaEtapaList!);

    const listEtapas = etapas?.filter((f) => f.nome?.toLowerCase() !== 'onboarding')!;
    const llistEtapas = listEtapas?.filter(
      (f) => !comandaEtapaList?.some((s) => s.tbEtapaId === f.id && s.idConsultorResponsavel! > 0),
    );
    setEtapasSelect(llistEtapas);

    consultorActions
      .list()
      .then((resp: any) => {
        setConsultores(resp.resultado);
      })
      .catch((err: any) => toast.warn(err));

    if (reuniaoId) {
      const reuniaoSelecionada = comandaEtapaList?.find((e) => e.id === Number(reuniaoId));
      if (reuniaoSelecionada)
        handleRowClick(
          {
            row: reuniaoSelecionada,
          } as any,
          {} as any,
          {} as any,
        );
    }
  }, [comanda, comandaEtapas, etapas]);

  const columns: GridColDef[] = [
    {
      field: 'idEtapa',
      headerName: translate('etapa'),
      minWidth: 100,
      flex: 1,
      editable: false,
      renderCell: (params: GridCellParams) => {
        const obj = params.row as IComandaEtapaReunioes;
        const etapaFind = etapas?.find((f) => f.id === obj.tbEtapaId);
        if (etapaFind) return etapaFind?.nome;
        return obj.onboarding ? 'Onboarding' : '';
      },
      valueGetter: (params: any, row: IComandaEtapaReunioes) => {
        const etapaFind = etapas?.find((f) => f.id === row.tbEtapaId);
        if (etapaFind) return etapaFind?.nome;
        return row.onboarding ? 'Onboarding' : '';
      },
    },
    {
      field: 'status',
      headerName: translate('status'),
      minWidth: 120,
      flex: 0.8,
      valueGetter(params: any, row: IComandaEtapaReunioes) {
        const reuniao = row;
        return handleStatus(reuniao.status, reuniao.statusValidacao, translate);
      },
    },
    {
      field: 'dtPrevista',
      headerName: translate('dataPrevista'),
      width: 100,
      editable: false,
      renderCell: (params: GridCellParams) => {
        let obj = params.row as IComandaEtapaReunioes;
        return formatarData(obj.dtPrevista);
      },
    },
    {
      field: 'dtRealizadaInicio',
      headerName: translate('horaInicio'),
      width: 80,
      editable: false,
      renderCell: (params: GridCellParams) => {
        let obj = params.row as IComandaEtapaReunioes;
        return moment(forcarTimezone(obj.dtRealizadaInicio!.toString()).toDate().toString()).format(
          'HH:mm',
        );
      },
    },
    {
      field: 'dtRealizadaFim',
      headerName: translate('horaFim'),
      width: 80,
      editable: false,
      renderCell: (params: GridCellParams) => {
        let obj = params.row as IComandaEtapaReunioes;
        return moment(forcarTimezone(obj.dtRealizadaFim!.toString()).toDate().toString()).format(
          'HH:mm',
        );
      },
    },
    {
      field: 'idConsultorResponsavel',
      headerName: translate('consultorA'),
      width: 120,
      renderCell: (params: GridCellParams) => {
        let obj = params.row as IComandaEtapaReunioes;
        return consultores?.find((f) => f.id === obj.idConsultorResponsavel)?.nome;
      },
    },
    {
      field: 'tempoTotal',
      headerName: translate('tempoTotal'),
      width: 50,
      renderCell: (params: GridCellParams) => {
        let obj = params.row as IComandaEtapaReunioes;
        return getTempoTotal(
          formatarHora(obj.dtRealizadaInicio),
          formatarHora(obj.dtRealizadaFim),
        );
      },
    },
    {
      field: 'acao',
      headerName: translate('acao'),
      width: 80,
      editable: true,
      renderCell: (params: GridCellParams) => {
        let obj = params.row as IComandaEtapaReunioes;
        return (
          <Icon sx={{ cursor: 'pointer' }} onClick={() => HandleApagar(obj.id!)}>
            {' '}
            <DeleteIcon />{' '}
          </Icon>
        );
      },
    },
  ];

  const getTempoTotal = (horaInicio: string, horaFim: string) => {
    let horaTotal = '';
    let hInicio = parse(horaInicio);
    let hFim = parse(horaFim);

    let diff = Math.abs(hFim - hInicio);
    if (diff !== 0) {
      let horas = Math.floor(diff / 60);
      let minutos = diff - horas * 60;
      horaTotal = horas + ':' + (minutos > 9 ? minutos : '0' + minutos);
    }

    return horaTotal;
  };

  function parse(horario: any) {
    let [hora, minuto] = horario.split(':').map((v: any) => parseInt(v));
    if (!minuto) {
      minuto = 0;
    }
    return minuto + hora * 60;
  }

  const forcarTimezone = (data: any) => {
    return dayjs(data);
  };

  const handleRowClick: GridEventListener<'rowClick'> = (params: any) => {
    const listEtapas = etapas?.filter((f) => f.nome?.toLowerCase() !== 'onboarding')!;

    setEtapasSelect(listEtapas);
    openDrawerReuniao(true);
    setEditando(true);
    setReuniao({
      ...reuniao,
      id: params.row.id,
      tbEtapaId: params.row.tbEtapaId,
      comandaId: params.row.comandaId,
      dtPrevista: forcarTimezone(params.row.dtPrevista),
      dtRealizadaInicio: forcarTimezone(params.row.dtRealizadaInicio).format(),
      dtRealizadaFim: forcarTimezone(params.row.dtRealizadaFim).format(),
      onboarding: params.row.onboarding,
      idConsultorResponsavel: params.row.idConsultorResponsavel,
      valorHora: params.row.valorHora,
      tempoTotal: params.row.tempoTotal,
      valorTotal: params.row.valorTotal,
      status: params.row.status,
    });
  };

  const handleCellClick: GridEventListener<'cellClick'> = (params: any, event: any) => {
    if (params.field === 'acao') {
      event.stopPropagation();
    }
  };

  const openDrawerReuniao = (result: any) => {

    if (result) {
      setReuniao({
        ...empty,
        comandaId: idComanda!,
        dtPrevista: dayjs(new Date()),
      });
      setEditando(false);
    }

    const listEtapas = etapas?.filter((f) => f.nome?.toLowerCase() !== 'onboarding')!;
    const comandaEtapaList = comandaEtapas?.filter(
      (f) => f.idConsultorResponsavel! > 0 && !f.onboarding,
    );

    const llistEtapas = listEtapas?.filter(
      (f) => !comandaEtapaList?.some((s) => s.tbEtapaId === f.id && s.idConsultorResponsavel! > 0),
    );
    setEtapasSelect(llistEtapas);

    const etapasComMetodologia = etapas?.map((e) => {
      return {
        id: e.id,
        nome: `${e.idMetodologiaNavigation?.nome ? e.idMetodologiaNavigation?.nome + ' | ' : +' | '
          } ${e.nome}`,
      };
    });
    setEtapasSelect(etapasComMetodologia! as IEtapa[]);

    setOpen(result);
    if (!result) setEditando(false);
  };

  const HandleApagar = (id: any) => {
    const sub = reunioes?.filter((f) => f.id !== id);
    reunioesActions
      .delete(id)
      .then(() => {
        setReunioes(sub);
      })
      .catch((e) => toast.error(e ?? translate('falhaAoDeletarReuniao')));
  };

  const adicionarNaLista = () => {
    setLoadingSave(true);

    if (validarReunião()) {
      toast.warn(translate('preenchaTodosOsCamposParaContinuar'));
      setLoadingSave(false);
      return;
    }

    let horaInicio = parse(formatarHora(reuniao.dtRealizadaInicio));
    let horaFim = parse(formatarHora(reuniao.dtRealizadaFim));

    if (horaInicio < horaFim) {
      if (reuniao?.id === 0) {
        reunioesActions
          .create(reuniao)
          .then((result) => {
            toast.success(translate('reuniaoAdicionada'));
            const newList = reunioes.concat(result);
            setReunioes(newList);
            carregarComanda();
            openDrawerReuniao(false);
            setReuniao(empty);
          })
          .catch(() => toast.error(translate('falhaAoAdicionarReuniao')));
      } else {
        reunioesActions
          .update(reuniao!.id!, reuniao)
          .then((result) => {
            const sub = reunioes?.filter((f) => f.id !== result.id);
            setReunioes(sub.concat(result));
            carregarComanda();
            openDrawerReuniao(false);
            setReuniao(empty);
            toast.success(translate('reuniaoAtualizada'));
          })
          .catch(() => { });
      }
    } else {
      toast.warn(translate('horaInicioNaoPodeSerMaiorQueHoraFim'));
    }

    setLoadingSave(false);
  };

  const validarReunião = () => {
    if (reuniao.dtPrevista === undefined) return true;
    if (reuniao.dtRealizadaInicio === undefined) return true;
    if (reuniao.dtRealizadaFim === undefined) return true;
    if (reuniao.tbEtapaId === undefined) return true;
    if (reuniao.idConsultorResponsavel === undefined) return true;
  };

  const patchReuniao = (status: StatusReuniaoEnum) => {
    const dataInicio = new Date((reuniao.dtPrevista as any).toDate());
    dataInicio.setHours(dayjs(reuniao.dtRealizadaInicio as any).hour() - 3);
    dataInicio.setMinutes(dayjs(reuniao.dtRealizadaInicio as any).minute());

    const dataFim = new Date((reuniao.dtPrevista as any).toDate());
    dataFim.setHours(dayjs(reuniao.dtRealizadaFim as any).hour() - 3);
    dataFim.setMinutes(dayjs(reuniao.dtRealizadaFim as any).minute());

    reunioesActions
      .patch(reuniao?.id!, {
        id: reuniao?.id,
        dataInicio,
        dataFim,
        status,
      })
      .then(() => {
        carregarComanda();
        toast.success(translate('reuniaoRevisaoSolicitada'));
      })
      .catch((e) => {
        carregarComanda();
        toast.error(e ?? translate('falhaAoConcluirReuniao'))
      });
  };

  const revisarReuniao = (comando: string) => {
    setOpen(false);
    switch (comando) {
      case 'concluir':
        patchReuniao(StatusReuniaoEnum.PENDENTE_APROVACAO);
        break;
      case 'noShow':
        patchReuniao(StatusReuniaoEnum.NO_SHOW);
        break;
      default:
        break;
    }
  };

  const handlePaginationModelChange = (newModel: any) => {
    setPaginationLoading(true);
    setPaginationModel(newModel);

    // Calcular o número de linhas visíveis na página atual
    const totalRows = reunioes?.length || 0;
    const startRow = newModel.page * newModel.pageSize;
    const rowsInCurrentPage = Math.min(newModel.pageSize, totalRows - startRow);
    setVisibleRows(rowsInCurrentPage > 0 ? rowsInCurrentPage : 1);

    // Simular um carregamento de dados
    setTimeout(() => {
      setPaginationLoading(false);
    }, 500);
  };

  // Calcular a altura da tabela com base no número de linhas visíveis
  const calculateTableHeight = () => {
    const rowHeight = 52;
    const headerHeight = 52;
    const footerHeight = 52;
    const safetyMargin = 6;

    if (visibleRows < 10) {
      // Se tiver menos de 10 linhas, ajustar a altura para o número exato de linhas
      return `${(visibleRows * rowHeight) + headerHeight + footerHeight + safetyMargin}px`;
    } else {
      // Caso contrário, usar a altura padrão para 10 linhas
      return '630px';
    }
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
            padding: '16px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {translate('responsavelPelasReunioes')}
            </Typography>
            <Button
              style={{
                padding: '7px',
                height: '35px',
                minWidth: '35px',
              }}
              onClick={() => openDrawerReuniao(true)}
              startIcon={<AddIcon />}
              variant="contained"
            >
              {translate('agendarReuniao')}
            </Button>
          </Box>

          <Box
            sx={{
              height: calculateTableHeight(),
              width: '100%',
            }}
          >
            <ThemeProvider theme={createTheme({}, locale)}>
              <DataGrid
                getRowId={(row) =>
                  // Usando o ID próprio da reunião que deve ser único
                  row.id ? `${row.id}` : `${row?.tbEtapaId}_${row?.comandaId}`
                }
                rows={reunioes ?? []}
                onCellClick={handleCellClick}
                onRowClick={handleRowClick}
                columns={columns}
                loading={loading || paginationLoading}
                initialState={{
                  pagination: {
                    paginationModel: paginationModel,
                  },
                }}
                pageSizeOptions={[5, 10, 25, 50]}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                disableColumnMenu={false}
                disableRowSelectionOnClick={false}
                density="standard"
                rowHeight={52}
                localeText={{
                  noRowsLabel: translate('nenhumaReuniaoEncontrada'),
                  footerRowSelected: (count) => `${count} ${translate('reuniaoSelecionada')}`,
                  footerTotalRows: translate('totalDeReunioes'),
                }}
                sx={{
                  borderRadius: '14px',
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    cursor: 'pointer',
                  },
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderTopLeftRadius: '14px',
                    borderTopRightRadius: '14px',
                  },
                  '@media (max-width: 600px)': {
                    '& .MuiDataGrid-columnHeaders': {
                      fontSize: '0.75rem',
                    },
                    '& .MuiDataGrid-cell': {
                      fontSize: '0.75rem',
                      padding: '0 8px',
                    },
                  },
                }}
                aria-label={translate('tabelaDeReunioes')}
              />
            </ThemeProvider>
          </Box>
        </Box>

        <DrawerReunioes
          adicionarNaLista={adicionarNaLista}
          consultores={consultores}
          editando={editando}
          setEditando={setEditando}
          etapasSelect={etapasSelect}
          idComanda={idComanda!}
          loadingSave={loadingSave}
          open={open}
          reuniao={reuniao}
          setOpen={setOpen}
          setReuniao={setReuniao}
          revisarReuniao={revisarReuniao}
          buComanda={comanda?.idBU}
          modeloComercial={
            ModeloComercial[comanda?.modeloComercial as keyof typeof ModeloComercial]
          }
        />
      </Box>
    </>
  );
};

const GridItemCadastro = styled(Grid)`
  padding-bottom: 20px;
`;

export const GridItemCadastroBtnSalvar = styled(GridItemCadastro)`
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
`;

export default ReuniaoComponent;
